<script lang="ts" setup>
import { useI18n } from "vue-i18n";

import { useFilters } from "@/composables";
import OmniTable from "@/components/omni/OmniTable.vue";
import InvoiceLink from "@/components/links/InvoiceLink.vue";
import OrderLink from "@/components/links/OrderLink.vue";
import InvoiceStatus from "@/components/statuses/InvoiceStatus.vue";
import InvoiceRouteAddress from "@/views/billing/invoices/components/InvoiceRouteAddress.vue";
import InvoiceBillingMethod from "@/views/billing/invoices/components/InvoiceBillingMethod.vue";

import type { Invoice, InvoiceCustomer } from "@/api/billing/dto/invoice";

const { t } = useI18n();
const { money } = useFilters();

const props = defineProps<{
  items: Invoice[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
}>();

const emits = defineEmits<{
  (e: "update:page", page: number): void;
  (e: "update:page-size", size: number): void;
}>();

const headers = [
  { title: t("billing.invoice"), key: "number", sortable: false, width: 140 },
  { title: t("billing.status"), key: "status", sortable: false, align: "center", width: 150 },
  { title: t("billing.order"), key: "order", sortable: false, width: 180 },
  { title: t("billing.route"), key: "route", sortable: false, width: 280 },
  { title: t("billing.bill-to-company"), key: "billToCompany", sortable: false },
  { title: t("billing.billing-method"), key: "billingMethod", sortable: false, width: 130 },
  { title: t("billing.batch"), key: "batch", sortable: false, width: 160 },
  { title: t("billing.total-rate"), key: "totalRate", sortable: false, align: "end", width: 150 },
  { title: "", key: "actions", sortable: false, align: "center", width: 92 },
];

function formatBillingAddress(customer: InvoiceCustomer): string {
  const stateWithZip = [customer.billingAddressState, customer.billingAddressZipCode].filter(Boolean).join(" ");

  return [customer.billingAddressLine, customer.billingAddressCity, stateWithZip].filter(Boolean).join(", ");
}
</script>

<template>
  <omni-table
    :headers="headers"
    :items="props.items"
    :items-length="props.total"
    :items-per-page="props.pageSize"
    :loading="props.loading"
    :mobile-breakpoint="0"
    :page="props.page"
    @update:page="emits('update:page', $event)"
    @update:items-per-page="emits('update:page-size', $event)"
  >
    <template #no-data>
      <div class="invoices-empty-state d-flex flex-column align-center justify-center row-gap-2">
        <span class="font-weight-medium text-grey-darken-3" data-qa="invoices-empty-title">
          {{ t("billing.no-invoices") }}
        </span>
        <span class="text-grey-darken-1" data-qa="invoices-empty-hint">{{ t("billing.no-invoices-hint") }}</span>
      </div>
    </template>

    <template #[`item.number`]="{ item }">
      <invoice-link :invoice-number="item.number" />
    </template>

    <template #[`item.status`]="{ item }">
      <v-tooltip :disabled="item.isActive" location="top">
        <template #activator="{ props: tooltipProps }">
          <invoice-status :is-active="item.isActive" :status="item.status" v-bind="tooltipProps" />
        </template>

        {{ t("billing.invoice-not-active", { number: item.number }) }}
      </v-tooltip>
    </template>

    <template #[`item.order`]="{ item }">
      <order-link :order-id="item.orderId" hide-label />
    </template>

    <template #[`item.route`]="{ item }">
      <invoice-route-address :stops="item.orderStops" />
    </template>

    <template #[`item.billToCompany`]="{ item }">
      <div class="d-flex flex-column invoices-table__stack">
        <span class="font-weight-600 text-grey-darken-3">{{ item.customer.name }}</span>
        <span class="invoices-table__secondary text-grey-darken-2">{{ formatBillingAddress(item.customer) }}</span>
      </div>
    </template>

    <template #[`item.billingMethod`]="{ item }">
      <invoice-billing-method :payment-type="item.customer.paymentType" />
    </template>

    <template #[`item.totalRate`]="{ item }">
      <span class="font-weight-medium text-grey-darken-4">{{ money(item.orderPaymentsTotal) }}</span>
    </template>

    <template #[`item.actions`]>
      <v-btn color="primary" data-qa="invoice-view" density="compact" icon="mdi-eye-outline" variant="text" />
    </template>
  </omni-table>
</template>

<style lang="scss" scoped>
.invoices-empty-state {
  min-height: 120px;
  line-height: 20px;
}

.invoices-table__stack {
  row-gap: 2px;
}

.invoices-table__secondary {
  font-size: 13px;
  line-height: 18px;
}
</style>
